// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { Tooltip } from '@mui/material';
import { windowOpenPopup } from '@shared/ui-kit/components/popups/window-open-popup/window-open-popup';
import {
  useShowAssetProfile,
  type AnalyzePopup,
} from '@shared/ui-kit/components/popups/asset-profile';
import { AssetLogo, type AssetLogoType } from '@shared/ui-kit/components';
import { classNames } from '@shared/ui-kit/utils';
import { type FC, type ReactNode, useCallback, useMemo } from 'react';

const ASSET_TYPES: Record<AssetLogoType, string[]> = {
  stock: ['common stock', 'stock'],
  etf: ['etf', 'etp'],
  mutual_fund: [
    'mutual_fund',
    'mutual fund',
    'fund',
    'pension-plan',
    'pension-plans',
    'eu_pension_plans',
    'us_pension_plans',
  ],
  crypto: ['crypto'],
};

export interface AssetBrandProps {
  readonly ticker: string;
  readonly assetType?: string;
  readonly label?: string;
  readonly url?: string;
  readonly logoUrl?: string;
  readonly showTooltip?: boolean;
  readonly className?: string;
  readonly watchlistButton?: ReactNode;
  readonly analyzePopup?: AnalyzePopup;
  readonly isMobile?: boolean;
  readonly isin?: string | null;
}

export const AssetBrand: FC<AssetBrandProps> = ({
  ticker,
  assetType = '',
  label,
  url,
  logoUrl,
  className = '',
  watchlistButton,
  analyzePopup,
  isMobile = false,
  isin,
}) => {
  const showAssetProfile = useShowAssetProfile();

  const identifiedAssetType = useMemo(() => {
    return Object.keys(ASSET_TYPES).find(key =>
      ASSET_TYPES[key as AssetLogoType].includes(assetType.toLowerCase()),
    );
  }, [assetType]) as AssetLogoType;

  const isStock = identifiedAssetType === 'stock';
  const isETF = identifiedAssetType === 'etf';
  const isMutualFund = identifiedAssetType === 'mutual_fund';

  const canShowProfile = isETF || isMutualFund;
  const canOpenUrl = isStock && !!url && url !== 'missing';

  const handleAction = useCallback(() => {
    if (isMobile) return;

    if (canOpenUrl) {
      windowOpenPopup(url);
    }

    if (canShowProfile) {
      showAssetProfile({
        ticker,
        assetType: identifiedAssetType,
        watchlistButton,
        analyzePopup,
      });
    }
  }, [
    canOpenUrl,
    canShowProfile,
    identifiedAssetType,
    showAssetProfile,
    ticker,
    url,
    watchlistButton,
    analyzePopup,
    isMobile,
  ]);

  const showIsin = !!(isin && isin !== ticker && isin !== 'missing');

  return (
    <div
      className={classNames(
        {
          'flex items-center gap-x-2': true,
          'cursor-pointer hover:underline hover:underline-offset-4':
            (canOpenUrl || canShowProfile) && !isMobile,
        },
        className,
      )}
      onClick={handleAction}
      data-testid={`asset-brand-${ticker}`}
    >
      <AssetLogo assetType={identifiedAssetType} logoUrl={logoUrl} />
      <div className="flex min-w-0 flex-col truncate">
        {label ? (
          <Tooltip
            title={label}
            placement="top"
            componentsProps={{
              tooltip: { className: '!uppercase' },
            }}
          >
            <span className="truncate !uppercase">{label}</span>
          </Tooltip>
        ) : (
          <span>{ticker ?? '-'}</span>
        )}
        {showIsin && <span className="text-sm">{isin}</span>}
      </div>
    </div>
  );
};
